import { formatDate } from '@angular/common';
import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { ApplicationContextStore } from '@raptor-fe/services';

const BRANCH_TIME_ZONE_SETTING_NAME = 'BranchTimeZone';

/**
 * Formats a date in the branch's configured time zone instead of the browser's, so any viewer sees
 * the store's local times. Falls back to the browser zone when the branch has no zone set.
 */
@Pipe({
    name: 'branchDate',
})
export class BranchDatePipe implements PipeTransform {
    private readonly appContext = inject(ApplicationContextStore);
    private readonly locale = inject(LOCALE_ID);

    transform(value: string | Date | null | undefined, format = 'short'): string {
        if (value == null || value === '') {
            return '';
        }

        const zone = this.appContext.settings()[BRANCH_TIME_ZONE_SETTING_NAME];
        const offset = zone ? offsetAt(new Date(value), zone) : undefined;

        return formatDate(value, format, this.locale, offset);
    }
}

/**
 * The zone's UTC offset at the given instant as an Angular timezone argument ("-0400"), undefined
 * when the zone is unknown to the browser.
 */
function offsetAt(date: Date, zone: string): string | undefined {
    try {
        const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName')?.value;

        const match = name?.match(/GMT([+-]\d{2}):(\d{2})/);

        return match ? `${match[1]}${match[2]}` : name === 'GMT' ? '+0000' : undefined;
    } catch {
        return undefined;
    }
}
